/**
 * DomainImpactSummary — Phase 5
 *
 * Groups creator artifact metrics by primary_domain.
 * Shows aggregated sessions, behavioral rate, and pensieve score per domain.
 * No ranking language, no comparison — purely informational.
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Layers } from "lucide-react";
import type { DomainStats } from "@/hooks/useCreatorAnalytics";

interface DomainImpactSummaryProps {
  domains: DomainStats[];
}

export function DomainImpactSummary({ domains }: DomainImpactSummaryProps) {
  if (domains.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Layers className="h-4 w-4" />
          Domain Impact Summary
        </CardTitle>
        <CardDescription>
          Aggregated metrics grouped by training domain
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Domain</TableHead>
                <TableHead className="text-right">Artifacts</TableHead>
                <TableHead className="text-right">Sessions</TableHead>
                <TableHead className="text-right">Avg Behavioral Rate</TableHead>
                <TableHead className="text-right">Avg Pensieve Score</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {domains.map(d => (
                <TableRow key={d.domain}>
                  <TableCell className="font-medium capitalize">
                    {d.domain}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {d.artifactCount}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {d.totalSessions.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {d.avgBehavioralRate > 0
                      ? `${Math.round(d.avgBehavioralRate * 100)}%`
                      : '—'}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {d.avgPensieveScore > 0
                      ? d.avgPensieveScore.toFixed(1)
                      : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
